import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import copy from 'clipboard-copy';
import { capitalize } from 'lodash/fp';

import {
    FieldHintText,
    FormInput,
    H2,
    Modal,
    ModalBody,
    ModalButton,
    ModalFooter,
    ModalHeader,
} from '@portal/common/components';
import { DataExplorerSelectionResource } from '@portal/common/types';
import { parseApiError } from '@portal/common/utility/api-helpers';
import { localizeConditions } from '@portal/common/utility/chart-data-helpers';

import api from '../../../api';
import config from '../../../config';
import _ from '../../../locale';
import { PERMALINK_PATH } from '../../../router/paths';
import { getOrganizationId } from '../../../store/organization/selectors';
import { getSessionAccountId } from '../../../store/session/selectors';

const MAX_PERMALINK_LENGTH = 128;

type Props = {
    isVisible: boolean;
    onClose: (isSubmitted: boolean) => void;
    dataExplorerSelection: DataExplorerSelectionResource;
};

function SharePermalinkModal({ isVisible, onClose, dataExplorerSelection }: Props) {
    const organizationId = useSelector(getOrganizationId);
    const memberId = useSelector(getSessionAccountId);

    const [permalink, setPermalink] = useState('');
    const [errors, setErrors] = useState<object | null>(null);

    const getShorter = useCallback(
        (string, initArr) => {
            let innerSting = string;
            if (innerSting.length > MAX_PERMALINK_LENGTH) {
                const innerArr = initArr.filter((item, index) => index !== initArr.length - 1);
                innerSting = innerArr.join(', ') + ' and other conditions';
                getShorter(innerSting, innerArr);
            } else {
                setPermalink(innerSting);
            }
        },
        [setPermalink]
    );

    const getPermalinkFormattedConditions = useCallback(() => {
        const initArr = localizeConditions(dataExplorerSelection.conditions).split(', ');
        const result = initArr.join(', ');

        getShorter(result, initArr);
    }, [dataExplorerSelection, getShorter]);

    const reset = useCallback(() => {
        getPermalinkFormattedConditions();
        setErrors(null);
    }, [getPermalinkFormattedConditions, setErrors]);

    const handleError = useCallback(
        (err) => {
            setErrors(parseApiError(err));
        },
        [setErrors]
    );

    const handleSubmit = useCallback(() => {
        api.permalink
            .createPermalink({
                name: permalink,
                organizationId,
                memberId,
                dataExplorerSelection,
            })
            .then((res) => {
                copy(config.clientUrl + PERMALINK_PATH + '/' + res.hash);
                setErrors(null);
                onClose(true);
            })
            .catch(handleError);
    }, [
        permalink,
        organizationId,
        memberId,
        dataExplorerSelection,
        onClose,
        handleError,
        setErrors,
    ]);

    const handleChange = useCallback(
        (ev) => {
            const value = ev.target.value;
            setPermalink(value);
            if (value.length > MAX_PERMALINK_LENGTH) {
                setErrors((prev) => ({
                    ...prev,
                    name: _('error_field_should_not_exceed_maximum_characters', {
                        field: 'Name',
                        max: MAX_PERMALINK_LENGTH,
                    }),
                }));
            }

            if (errors?.name && value.length <= MAX_PERMALINK_LENGTH) {
                setErrors((prev) => ({ ...prev, name: null }));
            }
        },
        [errors, setErrors, setPermalink]
    );

    const handleCancel = useCallback(() => onClose(false), [onClose]);

    const isSubmittable = useMemo(() => !!permalink && !errors?.name, [errors, permalink]);

    useEffect(reset, [isVisible]);

    if (!isVisible) return null;

    return (
        <Modal show={isVisible} onHide={onClose}>
            <ModalHeader>
                <H2>{capitalize(_('share_permalink_title'))}</H2>
            </ModalHeader>
            <ModalBody>
                <FormInput
                    required
                    size="large"
                    autoFocus
                    name="title"
                    error={errors && errors.name}
                    value={permalink}
                    onChange={handleChange}
                    label={_.get('share_permalink_input_label')}
                />
                <FieldHintText>{_.get('share_permalink_message')}</FieldHintText>
            </ModalBody>
            <ModalFooter>
                <ModalButton color="secondary" onClick={handleCancel}>
                    {_.get('cancel_button')}
                </ModalButton>
                <ModalButton onClick={handleSubmit} type="submit" disabled={!isSubmittable}>
                    {_.get('share_permalink_copy_btn')}
                </ModalButton>
            </ModalFooter>
        </Modal>
    );
}

export default SharePermalinkModal;
